import { yupResolver } from '@hookform/resolvers/yup'
import SaveIcon from '@mui/icons-material/Save'
import {
  Box,
  Button,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from '@mui/material'
import React, { useEffect } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { countries } from '~/src/constants'
import { ports } from '~/src/constants/ports'
import { TUserDetail } from '~/src/repositories/User/models/User'
import { destinationSchema } from '~/src/schemas'
import { TDestinationFormValues } from '~/src/types/form'

interface IDestinationFormProps {
  title: string
  id: number
  data: TUserDetail
  onSave: (id: number, formValue: TDestinationFormValues) => void
  isLoading: boolean
}

export const DestinationForm: React.FC<IDestinationFormProps> = ({
  title,
  id,
  data,
  onSave,
  isLoading,
}) => {
  const { t } = useTranslation()
  const {
    register,
    control,
    reset,
    handleSubmit,
    formState: { errors, isDirty },
  } = useForm<TDestinationFormValues>({
    resolver: yupResolver(destinationSchema),
    defaultValues: data,
    reValidateMode: 'onBlur',
  })

  useEffect(() => {
    reset(data)
  }, [data, reset])

  return (
    <Paper sx={{ mb: '20px' }}>
      <Typography
        variant={'h2'}
        sx={{
          padding: 2,
          fontSize: 20,
          fontStyle: 'normal',
          lineHeight: '32px',
          fontWeight: 'medium',
        }}
      >
        {title}
      </Typography>
      <Box
        sx={{
          borderTop: '1px solid rgba(224, 224, 224, 1)',
          padding: 2,
          borderBottom: '1px solid rgba(224, 224, 224, 1)',
        }}
      >
        <Box>
          <Typography
            variant={'h3'}
            sx={{
              fontWeight: 'medium',
              fontStyle: 'normal',
              fontSize: 20,
              lineHeight: '32px',
              mb: '24px',
            }}
          >
            {t('user_detail.consignee')}
          </Typography>
          <Grid container spacing={2}>
            <Grid container item spacing={2}>
              <Grid item lg={3}>
                <TextField
                  fullWidth
                  required
                  variant="outlined"
                  size="small"
                  id="outlined-adornment-company-name"
                  label={t('user_detail.company_name')}
                  {...register('consignee.companyName')}
                  error={errors.consignee?.companyName ? true : false}
                  helperText={errors.consignee?.companyName?.message}
                />
              </Grid>
              <Grid item lg={3}>
                <TextField
                  fullWidth
                  required
                  variant="outlined"
                  size="small"
                  id="outlined-adornment-name"
                  label={t('user_detail.name')}
                  {...register('consignee.name')}
                  error={errors.consignee?.name ? true : false}
                  helperText={errors.consignee?.name?.message}
                />
              </Grid>
              <Grid item lg={6}>
                <TextField
                  fullWidth
                  required
                  variant="outlined"
                  size="small"
                  id="outlined-adornment-email"
                  label={t('user_detail.email')}
                  {...register('consignee.email')}
                  error={errors.consignee?.email ? true : false}
                  helperText={errors.consignee?.email?.message}
                />
              </Grid>
            </Grid>
            <Grid container item spacing={2}>
              <Grid item lg={6}>
                <TextField
                  fullWidth
                  required
                  variant="outlined"
                  size="small"
                  id="outlined-adornment-address-1"
                  label={t('user_detail.address_1')}
                  {...register('consignee.address1')}
                  error={errors.consignee?.address1 ? true : false}
                  helperText={errors.consignee?.address1?.message}
                />
              </Grid>
              <Grid item lg={6}>
                <TextField
                  fullWidth
                  variant="outlined"
                  size="small"
                  id="outlined-adornment-address-2"
                  label={t('user_detail.address_2')}
                  {...register('consignee.address2')}
                  error={errors.consignee?.address2 ? true : false}
                  helperText={errors.consignee?.address2?.message}
                />
              </Grid>
            </Grid>
            <Grid container item spacing={2}>
              <Grid item lg={3}>
                <FormControl variant="outlined" size="small" fullWidth>
                  <InputLabel htmlFor="outlined-adornment-country">
                    {t('user_detail.country')}
                  </InputLabel>
                  <Controller
                    name="consignee.countryId"
                    control={control}
                    render={({ field: { onChange, value } }) => (
                      <Select
                        required
                        MenuProps={{
                          style: {
                            marginTop: 2,
                            maxHeight: '410px',
                            maxWidth: '200px',
                          },
                        }}
                        labelId="outlined-adornment-country"
                        id="outlined-adornment-country"
                        label={t('user_detail.country')}
                        value={value}
                        onChange={onChange}
                      >
                        {countries.map((country, index) => (
                          <MenuItem key={index} value={`${country.id}`}>
                            {country.name}
                          </MenuItem>
                        ))}
                      </Select>
                    )}
                  />
                </FormControl>
              </Grid>
              <Grid item lg={3}>
                <TextField
                  fullWidth
                  required
                  variant="outlined"
                  size="small"
                  id="outlined-adornment-crn"
                  label={t('user_detail.crn_no.')}
                  {...register('consignee.crnNo')}
                  error={errors.consignee?.crnNo ? true : false}
                  helperText={errors.consignee?.crnNo?.message}
                />
              </Grid>
              <Grid item lg={3}>
                <TextField
                  fullWidth
                  variant="outlined"
                  size="small"
                  required
                  id="outlined-adornment-phone"
                  label={t('user_detail.phone')}
                  {...register('consignee.tel')}
                  error={errors.consignee?.tel ? true : false}
                  helperText={errors.consignee?.tel?.message}
                />
              </Grid>
              <Grid item lg={3}>
                <TextField
                  fullWidth
                  variant="outlined"
                  size="small"
                  id="outlined-adornment-fax"
                  label={t('user_detail.fax')}
                  {...register('consignee.fax')}
                  error={errors.consignee?.fax ? true : false}
                  helperText={errors.consignee?.fax?.message}
                />
              </Grid>
            </Grid>
            <Grid container item spacing={2}>
              <Grid item lg={6}>
                <FormControl variant="outlined" size="small" fullWidth>
                  <InputLabel htmlFor="outlined-adornment-destination-port-1">
                    {t('user_detail.destination_port_1')}
                  </InputLabel>
                  <Controller
                    name="consignee.portOfDischargeId"
                    control={control}
                    render={({ field: { onChange, value } }) => (
                      <Select
                        MenuProps={{
                          style: {
                            marginTop: 2,
                            maxHeight: '410px',
                            maxWidth: '200px',
                          },
                        }}
                        labelId="outlined-adornment-destination-port-1"
                        id="outlined-adornment-destination-port-1"
                        label={t('user_detail.destination_port_1')}
                        value={value}
                        onChange={onChange}
                      >
                        {ports.map((p, i) => (
                          <MenuItem key={`port-of-discharge-${i}`} value={p.id}>
                            {p.name}
                          </MenuItem>
                        ))}
                      </Select>
                    )}
                  />
                </FormControl>
              </Grid>
              <Grid item lg={6}>
                <FormControl variant="outlined" size="small" fullWidth>
                  <InputLabel htmlFor="outlined-adornment-destination-port-2">
                    {t('user_detail.destination_port_2')}
                  </InputLabel>
                  <Controller
                    name="consignee.finalDestinationId"
                    control={control}
                    render={({ field: { onChange, value } }) => (
                      <Select
                        MenuProps={{
                          style: {
                            marginTop: 2,
                            maxHeight: '410px',
                            maxWidth: '200px',
                          },
                        }}
                        labelId="outlined-adornment-destination-port-2"
                        id="outlined-adornment-destination-port-2"
                        label={t('user_detail.destination_port_2')}
                        value={value}
                        onChange={onChange}
                      >
                        {ports.map((p, i) => (
                          <MenuItem key={`final-destination-${i}`} value={p.id}>
                            {p.name}
                          </MenuItem>
                        ))}
                      </Select>
                    )}
                  />
                </FormControl>
              </Grid>
            </Grid>
          </Grid>
        </Box>
        <Box mt={4}>
          <Typography
            variant={'h3'}
            sx={{
              fontWeight: 'medium',
              fontStyle: 'normal',
              fontSize: 20,
              lineHeight: '32px',
              mb: '24px',
            }}
          >
            {t('user_detail.notify_party')}
          </Typography>
          <Grid container spacing={2}>
            <Grid container item spacing={2}>
              <Grid item lg={3}>
                <TextField
                  fullWidth
                  required
                  variant="outlined"
                  size="small"
                  id="outlined-adornment-company-name"
                  label={t('user_detail.company_name')}
                  {...register('notifyParty.companyName')}
                  error={errors.notifyParty?.companyName ? true : false}
                  helperText={errors.notifyParty?.companyName?.message}
                />
              </Grid>
              <Grid item lg={3}>
                <TextField
                  fullWidth
                  required
                  variant="outlined"
                  size="small"
                  id="outlined-adornment-name"
                  label={t('user_detail.name')}
                  {...register('notifyParty.name')}
                  error={errors.notifyParty?.name ? true : false}
                  helperText={errors.notifyParty?.name?.message}
                />
              </Grid>
              <Grid item lg={6}>
                <TextField
                  fullWidth
                  required
                  variant="outlined"
                  size="small"
                  id="outlined-adornment-email"
                  label={t('user_detail.email')}
                  {...register('notifyParty.email')}
                  error={errors.notifyParty?.email ? true : false}
                  helperText={errors.notifyParty?.email?.message}
                />
              </Grid>
            </Grid>
            <Grid container item spacing={2}>
              <Grid item lg={6}>
                <TextField
                  fullWidth
                  required
                  variant="outlined"
                  size="small"
                  id="outlined-adornment-address-1"
                  label={t('user_detail.address_1')}
                  {...register('notifyParty.address1')}
                  error={errors.notifyParty?.address1 ? true : false}
                  helperText={errors.notifyParty?.address1?.message}
                />
              </Grid>
              <Grid item lg={6}>
                <TextField
                  fullWidth
                  variant="outlined"
                  size="small"
                  id="outlined-adornment-address-2"
                  label={t('user_detail.address_2')}
                  {...register('notifyParty.address2')}
                  error={errors.notifyParty?.address2 ? true : false}
                  helperText={errors.notifyParty?.address2?.message}
                />
              </Grid>
            </Grid>
            <Grid container item spacing={2}>
              <Grid item lg={3}>
                <FormControl variant="outlined" size="small" fullWidth>
                  <InputLabel htmlFor="outlined-adornment-country">
                    {t('user_detail.country')}
                  </InputLabel>
                  <Controller
                    name="notifyParty.countryId"
                    control={control}
                    render={({ field: { onChange, value } }) => (
                      <Select
                        required
                        MenuProps={{
                          style: {
                            marginTop: 2,
                            maxHeight: '410px',
                            maxWidth: '200px',
                          },
                        }}
                        labelId="outlined-adornment-country"
                        id="outlined-adornment-country"
                        label={t('user_detail.country')}
                        value={value}
                        onChange={onChange}
                      >
                        {countries.map((country, index) => (
                          <MenuItem key={index} value={`${country.id}`}>
                            {country.name}
                          </MenuItem>
                        ))}
                      </Select>
                    )}
                  />
                </FormControl>
              </Grid>
              <Grid item lg={3}>
                <TextField
                  fullWidth
                  required
                  variant="outlined"
                  size="small"
                  id="outlined-adornment-crn"
                  label={t('user_detail.crn_no.')}
                  {...register('notifyParty.crnNo')}
                  error={errors.notifyParty?.crnNo ? true : false}
                  helperText={errors.notifyParty?.crnNo?.message}
                />
              </Grid>
              <Grid item lg={3}>
                <TextField
                  fullWidth
                  required
                  variant="outlined"
                  size="small"
                  id="outlined-adornment-phone"
                  label={t('user_detail.phone')}
                  {...register('notifyParty.tel')}
                  error={errors.notifyParty?.tel ? true : false}
                  helperText={errors.notifyParty?.tel?.message}
                />
              </Grid>
              <Grid item lg={3}>
                <TextField
                  fullWidth
                  variant="outlined"
                  size="small"
                  id="outlined-adornment-fax"
                  label={t('user_detail.fax')}
                  {...register('notifyParty.fax')}
                  error={errors.notifyParty?.fax ? true : false}
                  helperText={errors.notifyParty?.fax?.message}
                />
              </Grid>
            </Grid>
          </Grid>
        </Box>
      </Box>
      <Box display={'flex'} justifyContent={'flex-end'} sx={{ padding: 2 }}>
        <Button
          variant="contained"
          onClick={handleSubmit((v) => onSave(id, v))}
          disabled={!isDirty || isLoading}
        >
          <SaveIcon fontSize={'small'} />
          <Typography
            sx={{
              fontSize: 15,
              fontStyle: 'normal',
              fontWeight: 'medium',
              lineHeight: '26px',
              color: 'white',
              ml: '11px',
            }}
          >
            {t('user_detail.save')}
          </Typography>
        </Button>
      </Box>
    </Paper>
  )
}
